import MojitoCore from 'mojito/core';
import MojitoPresentation from 'mojito/presentation';
import MojitoModules from 'mojito/modules';
import MojitoServices from 'mojito/services';
import { bindAll, isEmpty } from 'mojito/utils';
import AppOverlayTypes from './types';
import { SLIDING_VIEW_TYPES } from 'applications/sports/app/view/types.js';
import ToastOverlay from './toast-overlay/index.jsx';
import Search from 'applications/sports/components/search/index.jsx';
import SearchOverlay from 'applications/sports/components/search/search-overlay/index.jsx';
import { renderOverlayItem } from 'applications/common/utils/index.jsx';
import {
    selectActiveSlidingViewType,
    selectActiveSlidingViewData,
} from 'applications/sports/stores/application/selectors.js';
import { connect } from 'react-redux';

const { FlexPane } = MojitoPresentation.Components;
const SlidingBetslip = MojitoModules.SlidingBetslip.module;
const YourBets = MojitoModules.YourBets.module;
const AZMenu = MojitoModules.AZMenu;
const InactivityModule = MojitoModules.Inactivity.module;
const SlidingPane = MojitoPresentation.Components.SlidingPane.component;
const Login = MojitoModules.Login.module;
const ForeignWindow = MojitoModules.ForeignWindow.module;
const MyAccount = MojitoModules.MyAccount.module;
const { UIViewImplementation } = MojitoCore.Presentation;
const IntentTypes = MojitoPresentation.Base.Intent.Types;
const intentActions = MojitoCore.Intents.actions;
const { dispatch } = MojitoCore.Services.redux.store;
const { APP_OVERLAY_ITEMS } = AppOverlayTypes;
const DialogMessage = MojitoModules.PlayerMessages.DialogMessage.module;
const AlertMessage = MojitoModules.PlayerMessages.AlertMessage.module;
const { selectIsBuilding } = MojitoModules.MatchAcca.selectors;
const isShallowEqual = MojitoCore.Base.objUtils.isShallowEqual;
const { selectLoginState } = MojitoServices.Authentication.selectors;
const { STATES: AUTH_STATES } = MojitoServices.Authentication.types;

class AppOverlay extends UIViewImplementation {
    constructor(component) {
        super(component);

        bindAll(
            this,
            'renderSlidingAZMenu',
            'renderSlidingBetslip',
            'renderSlidingOpenBets',
            'renderForeignWindow',
            'renderMyAccount',
            'renderInactivityModule'
        );

        this.itemRenderers = {
            [APP_OVERLAY_ITEMS.ALERT_MESSAGE]: () => <AlertMessage key={'alertMessage'} />,
            [APP_OVERLAY_ITEMS.AZ_MENU]: this.renderSlidingAZMenu,
            [APP_OVERLAY_ITEMS.DIALOG_MESSAGE]: () => <DialogMessage key={'dialogMessage'} />,
            [APP_OVERLAY_ITEMS.FOREIGN_WINDOW]: this.renderForeignWindow,
            [APP_OVERLAY_ITEMS.INACTIVITY]: this.renderInactivityModule,
            [APP_OVERLAY_ITEMS.LOGIN]: () => <Login key={'login'} />,
            [APP_OVERLAY_ITEMS.MY_ACCOUNT]: this.renderMyAccount,
            [APP_OVERLAY_ITEMS.SLIDING_BETSLIP]: this.renderSlidingBetslip,
            [APP_OVERLAY_ITEMS.SLIDING_OPEN_BETS]: this.renderSlidingOpenBets,
            [APP_OVERLAY_ITEMS.TOAST_ITEMS]: () => <ToastOverlay key="toast-overlay" />,
        };
    }

    shouldComponentUpdate(nextProps, nextState) {
        return !isShallowEqual(this.props, nextProps) || !isShallowEqual(this.state, nextState);
    }

    onCloseAZMenu() {
        dispatch(intentActions.publishIntent(IntentTypes.HIDE_SLIDING_AZ_MENU));
    }

    onCloseOpenBets() {
        dispatch(intentActions.publishIntent(IntentTypes.HIDE_SLIDING_OPEN_BETS));
    }

    onCloseMyAccount() {
        dispatch(intentActions.publishIntent(IntentTypes.HIDE_ACCOUNT_POPUP));
    }

    renderInactivityModule() {
        if (this.props.loginState !== AUTH_STATES.LOGGED_IN) {
            return null;
        }
        return <InactivityModule key={'inactivity'} />;
    }

    renderForeignWindow() {
        if (this.props.activeSlidingViewType !== SLIDING_VIEW_TYPES.FOREIGN_WINDOW) {
            return null;
        }

        return <ForeignWindow content={this.props.activeSlidingViewData} key={'foreignWindow'} />;
    }

    renderMyAccount() {
        if (this.props.activeSlidingViewType !== SLIDING_VIEW_TYPES.MY_ACCOUNT) {
            return null;
        }

        const { selectedMenuId } = this.props.activeSlidingViewData || {};
        return (
            <MyAccount
                selectedMenuId={selectedMenuId}
                onClose={this.onCloseMyAccount}
                key={'myAccount'}
            />
        );
    }

    renderSlidingBetslip() {
        const isOpen = this.props.activeSlidingViewType === SLIDING_VIEW_TYPES.BETSLIP;
        const data = this.props.activeSlidingViewData || {};
        return (
            <SlidingBetslip
                key={'slidingBetslip'}
                isOpen={isOpen}
                betslipType={data.betslipType}
                isBetslipButtonMinimized={this.props.isBetslipButtonMinimized}
            />
        );
    }

    renderSlidingOpenBets() {
        const isOpen = this.props.activeSlidingViewType === SLIDING_VIEW_TYPES.OPEN_BETS;
        const { slidingPane, openBetsContainer } = this.config.slidingOpenBets;
        return (
            <SlidingPane
                key={'slidingOpenBets'}
                isOpen={isOpen}
                config={slidingPane}
                onClickOutside={this.onCloseOpenBets}
                onCloseClick={this.onCloseOpenBets}
            >
                <FlexPane config={openBetsContainer}>
                    <YourBets onSelectionClick={this.onCloseOpenBets} />
                </FlexPane>
            </SlidingPane>
        );
    }

    renderSearch(isInitiallyActive = false) {
        return (
            <Search initiallyActive={isInitiallyActive} config={this.config.searchControllerView} />
        );
    }

    renderSearchOverlay(onSearchClose, isInitiallyActive = false) {
        return isInitiallyActive ? (
            <SearchOverlay isOpen={isInitiallyActive} onSearchClose={onSearchClose} />
        ) : null;
    }

    renderSlidingAZMenu() {
        const isOpen = this.props.activeSlidingViewType === SLIDING_VIEW_TYPES.AZ_MENU;
        const { slidingPane } = this.config.slidingAZMenu;
        return (
            <SlidingPane
                key={'azMenu'}
                config={slidingPane}
                isOpen={isOpen}
                onCloseClick={this.onCloseAZMenu}
                onClickOutside={this.onCloseAZMenu}
            >
                <AZMenu
                    onClose={this.onCloseAZMenu}
                    renderSearchComponent={this.renderSearch}
                    renderSearchOverlay={isOpen ? this.renderSearchOverlay : undefined}
                />
            </SlidingPane>
        );
    }

    render() {
        if (isEmpty(this.config.items)) {
            return null;
        }

        return this.config.items.map(itemDescriptor =>
            renderOverlayItem(this.itemRenderers, itemDescriptor, this)
        );
    }
}

const mapStateToProps = state => ({
    activeSlidingViewType: selectActiveSlidingViewType(state),
    activeSlidingViewData: selectActiveSlidingViewData(state),
    isBetslipButtonMinimized: selectIsBuilding(state),
    loginState: selectLoginState(state),
});

export default connect(mapStateToProps)(AppOverlay);
